import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Filter, ChevronDown, ChevronUp, Check } from 'lucide-react';
import CustomSelect from './CustomSelect';

export default function BaseFilter({
    fields = [],
    onFilter,
    onReset,
    initialFilters = {},
    showAdvancedFilters = false,
    searchPlaceholder = 'Tìm kiếm...',
    showSearchButton = true,
    autoApply = false,
    extraActions = null
}) {
    const searchField = fields.find(f => f.isSearch || f.type === 'search');
    const advancedFields = fields.filter(f => !f.isSearch && f.type !== 'search');

    const [filters, setFilters] = useState(initialFilters);
    const [showFilters, setShowFilters] = useState(showAdvancedFilters);
    const popoverRef = useRef(null);

    useEffect(() => {
        setFilters(initialFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(initialFilters)]);

    // Khóa cuộn trang khi mở bộ lọc dạng drawer trên màn hình mobile
    useEffect(() => {
        if (showFilters && typeof window !== 'undefined' && window.innerWidth < 640) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [showFilters]);

    // Close popover on click outside - only active when popover is open
    useEffect(() => {
        if (!showFilters) return;

        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                if (!autoApply) {
                    setFilters((prev) => {
                        const next = { ...initialFilters };
                        if (searchField) {
                            next[searchField.name] = prev[searchField.name];
                        }
                        return next;
                    });
                }
                setShowFilters(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showFilters, initialFilters, autoApply, searchField]);

    const toggleFilters = () => {
        if (!showFilters && !autoApply) {
            setFilters((prev) => {
                const next = { ...initialFilters };
                if (searchField) {
                    next[searchField.name] = prev[searchField.name];
                }
                return next;
            });
        }
        setShowFilters((prev) => !prev);
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);

        if (autoApply) {
            onFilter?.(newFilters);
        }
    };

    const handleApplyFilters = () => {
        onFilter?.(filters);
        setShowFilters(false);
    };

    const handleCancel = () => {
        if (!autoApply) {
            setFilters((prev) => {
                const next = { ...initialFilters };
                if (searchField) {
                    next[searchField.name] = prev[searchField.name];
                }
                return next;
            });
        }
        setShowFilters(false);
    };

    const hasActiveFilters = () => {
        return Object.entries(filters).some(([key, value]) => {
            if (
                key === 'pageSize' ||
                key === 'sortTime' ||
                key === 'sortBy' ||
                key === 'sortOrder' ||
                key === searchField?.name
            ) {
                return false;
            }
            if (value === undefined || value === null || value === '') {
                return false;
            }
            if (typeof value === 'string' && (value.trim().toLowerCase() === 'all' || value.trim() === '')) {
                return false;
            }

            const field = advancedFields.find((f) => f.name === key);
            if (field) {
                // Nếu field có khai báo defaultValue và giá trị khớp với defaultValue
                if (field.defaultValue !== undefined && value === field.defaultValue) {
                    return false;
                }
                // Đối với các select trạng thái chỉ có [Hoạt động (true), Không hoạt động (false)], mặc định hiển thị là true
                const hasEmptyOption = field.options?.some(
                    (opt) => opt.value === '' || opt.value === null || opt.value === undefined || opt.value === 'all'
                );
                if (!hasEmptyOption && field.options?.length === 2 && value === true) {
                    return false;
                }
            } else if (key === 'isActive' && value === true) {
                return false;
            }

            return true;
        });
    };

    const renderFieldControl = (field) => {
        const { name, type, options, placeholder } = field;

        switch (type) {
            case 'select':
                return (
                    <CustomSelect
                        value={filters[name] ?? ''}
                        onChange={(value) => handleFilterChange(name, value)}
                        options={options || []}
                        placeholder={placeholder || 'Tất cả'}
                        className="w-full"
                    />
                );

            case 'checkbox':
                return (
                    <label className="flex items-center space-x-2 cursor-pointer py-1">
                        <input
                            type="checkbox"
                            checked={filters[name] || false}
                            onChange={(e) => handleFilterChange(name, e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-xs sm:text-sm font-medium text-gray-700">{field.label}</span>
                    </label>
                );

            case 'number':
                return (
                    <input
                        type="number"
                        value={filters[name] || ''}
                        onChange={(e) => handleFilterChange(name, Number(e.target.value))}
                        placeholder={placeholder}
                        className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                );

            case 'date':
                return (
                    <input
                        type="date"
                        value={filters[name] || ''}
                        onChange={(e) => handleFilterChange(name, e.target.value)}
                        className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                );

            case 'custom':
                return field.render ? field.render({ filters, handleFilterChange, setFilters }) : null;

            case 'text':
            case 'search':
            default:
                return (
                    <input
                        type="text"
                        value={filters[name] || ''}
                        onChange={(e) => handleFilterChange(name, e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleApplyFilters();
                            }
                        }}
                        placeholder={placeholder}
                        className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                );
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-xs border border-gray-200/80 mb-4">
            <div className="p-3 sm:px-4 sm:py-3">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
                    {searchField && (
                        <div className="relative flex-1 min-w-0 sm:max-w-xs md:sm:max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                value={filters[searchField.name] || ''}
                                onChange={(e) => handleFilterChange(searchField.name, e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleApplyFilters();
                                    }
                                }}
                                placeholder={searchField.placeholder || searchPlaceholder}
                                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                            {filters[searchField.name] && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newFilters = { ...filters, [searchField.name]: '' };
                                        setFilters(newFilters);
                                        onFilter?.(newFilters);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                        {advancedFields.length > 0 && (
                            <div className="relative flex-1 sm:flex-initial" ref={popoverRef}>
                                <button
                                    type="button"
                                    onClick={toggleFilters}
                                    className={`w-full sm:w-auto relative inline-flex items-center justify-center gap-1.5 px-3.5 sm:px-4 py-2 text-sm font-medium rounded-lg border transition-all cursor-pointer ${
                                        showFilters || hasActiveFilters()
                                            ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-xs'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="relative flex items-center">
                                        <Filter className="w-4 h-4" />
                                        {hasActiveFilters() && (
                                            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" />
                                        )}
                                    </div>
                                    <span>Bộ lọc</span>
                                    {showFilters ? (
                                        <ChevronUp className="w-4 h-4 text-gray-500" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                    )}
                                </button>

                                {/* Dropdown Popover on Desktop / Bottom Sheet Drawer on Mobile */}
                                {showFilters && (
                                    <>
                                        {/* Mobile Backdrop Overlay */}
                                        <div
                                            className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 sm:hidden animate-in fade-in duration-200"
                                            onClick={handleCancel}
                                        />

                                        {/* Dropdown Container */}
                                        <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl border-t border-gray-200 p-4 max-h-[85vh] flex flex-col animate-in slide-in-from-bottom duration-200 sm:absolute sm:inset-auto sm:left-0 sm:top-full sm:mt-2 sm:w-[480px] sm:max-w-[calc(100vw-32px)] sm:max-h-none sm:rounded-2xl sm:border sm:border-gray-200/90 sm:p-5 sm:animate-in sm:fade-in sm:zoom-in-95 sm:duration-150 sm:overflow-visible">
                                            {/* Mobile Drawer Header */}
                                            <div className="sm:hidden mb-3">
                                                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
                                                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                                                    <div className="flex items-center gap-2 font-semibold text-gray-900 text-base">
                                                        <Filter className="w-4 h-4 text-blue-600" />
                                                        <span>Bộ lọc nâng cao</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {onReset && hasActiveFilters() && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    onReset();
                                                                    setShowFilters(false);
                                                                }}
                                                                className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded"
                                                            >
                                                                Đặt lại
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={handleCancel}
                                                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Filter Fields List */}
                                            <div className="space-y-3.5 overflow-y-auto flex-1 py-2 px-0.5 pb-20 sm:pb-0 sm:overflow-visible sm:py-0">
                                                {advancedFields.map((field) => (
                                                    <div
                                                        key={field.name}
                                                        className={field.fullWidth ? "w-full" : "flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3"}
                                                    >
                                                        {!field.noLabel && field.type !== 'checkbox' && (
                                                            <label className="w-full sm:w-28 md:sm:w-32 text-xs sm:text-sm font-medium text-gray-700 shrink-0 text-left">
                                                                {field.label}
                                                            </label>
                                                        )}
                                                        <div className="flex-1 min-w-0 w-full">
                                                            {renderFieldControl(field)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Drawer / Popover Actions Footer */}
                                            <div className="mt-4 pt-3.5 border-t border-gray-100 flex items-center justify-end gap-2.5 sm:gap-3 shrink-0">
                                                {onReset && hasActiveFilters() && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            onReset();
                                                            setShowFilters(false);
                                                        }}
                                                        className="hidden sm:inline-block mr-auto text-xs sm:text-sm text-gray-500 hover:text-red-600 font-medium transition-colors cursor-pointer"
                                                    >
                                                        Xóa bộ lọc
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={handleCancel}
                                                    className="flex-1 sm:flex-none justify-center px-4 py-2.5 sm:py-2 text-gray-700 hover:bg-gray-100 border border-gray-200 sm:border-transparent rounded-xl sm:rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 cursor-pointer"
                                                >
                                                    <X className="w-4 h-4" />
                                                    <span>Hủy bỏ</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleApplyFilters}
                                                    className="flex-1 sm:flex-none justify-center px-5 py-2.5 sm:py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs sm:text-sm font-semibold rounded-xl sm:rounded-lg shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    <span>Đồng ý</span>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {showSearchButton && (
                            <button
                                type="button"
                                onClick={handleApplyFilters}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors cursor-pointer"
                            >
                                <Search className="w-4 h-4" />
                                <span>Tìm kiếm</span>
                            </button>
                        )}

                        {extraActions && <div className="sm:ml-auto flex-shrink-0">{extraActions}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
